// @flow

import React from "react";
import classNames from "classnames";
import { localize } from "redux-i18n";
import { View, Text } from "@components";

import optionResolver from "../utils/options";
import type { SchemaTableWidgetProps } from "../types";

import styles from "./textarea.scss";

export type TextareaWidgetStateState = $Exact<{
  touched: boolean,
}>;

export class TextareaWidgetState extends React.Component<
  SchemaTableWidgetProps<string>,
  TextareaWidgetStateState,
> {
  state = {
    touched: false,
  };

  componentDidUpdate(prevProps: SchemaTableWidgetProps<string>) {
    // this will only happen as part of a request to hide errors
    if (!this.props.touched && prevProps.touched) {
      this.setState({ touched: false });
    }
  }

  handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
    this.props.onChange(event.currentTarget.value || "");
  };

  handleTouched = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ touched: true });
  };

  hasUiSchemaError = (value: string): boolean => {
    if (!this.state.touched) {
      return false;
    }

    if (this.props.required && (!value || !value.trim().length)) {
      return true;
    }

    const options = this.props.options || {};

    const minLength = optionResolver.resolveNumber(options.minLength, this.props.row);
    if (minLength && value && value.length < minLength) {
      return true;
    }

    const maxLength = optionResolver.resolveNumber(options.maxLength, this.props.row);
    if (maxLength && value && value.length > maxLength) {
      return true;
    }

    return false;
  };

  hasError = (value: string): boolean => {
    return !!this.props.error || this.hasUiSchemaError(value);
  };

  get remaining(): number | null {
    const resolvedMaxLength = optionResolver.resolveNumber(
      (this.props.options || {}).maxLength,
      this.props.row,
    );
    const maxLength =
      typeof resolvedMaxLength === "number" ? resolvedMaxLength : this.props.maxLength;
    return typeof maxLength === "number"
      ? Math.max(0, maxLength - (this.props.data || "").length)
      : null;
  }

  render() {
    const disabled = optionResolver.resolveBoolean(
      (this.props.options || {}).disabled,
      this.props.row,
    );

    if (!this.props.active) {
      return (
        <div className={classNames("text__readonly", { text__disabled: disabled })}>
          {this.props.data}
        </div>
      );
    }

    return (
      <View flex={1}>
        <textarea
          className={classNames("textarea__container form-control", {
            textarea__disabled: disabled,
            textarea__error: this.hasError(this.props.data),
          })}
          value={this.props.data || ""}
          required={this.props.required}
          disabled={disabled}
          onChange={this.handleChange}
          onBlur={this.handleTouched}
        >
          {this.props.data}
        </textarea>
        {this.remaining !== null && (
          <View alignSelf="flex-end">
            <Text size={0.75} color="black">
              {this.props.t("CHARACTERS_REMAINING", { count: this.remaining })}
            </Text>
          </View>
        )}
      </View>
    );
  }
}

export default localize()(TextareaWidgetState);
